import React, { useCallback, useState } from 'react';
import { Box, Button, IconButton, InputAdornment, TextField } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { type ChatStatus } from '../model/chatTypes';

interface ChatInputProps {
  status: ChatStatus;
  isTyping: boolean;
  onSendMessage: (text: string) => void;
  onNewChat?: () => void;
}

export const ChatInput: React.FC<ChatInputProps> = ({ status, isTyping, onSendMessage, onNewChat }) => {
  const [value, setValue] = useState('');

  const isResolved = status === 'resolved';
  const isSendDisabled = !value.trim() || isTyping || isResolved;

  const handleSend = useCallback(() => {
    if (!value.trim() || isTyping || isResolved) return;
    onSendMessage(value);
    setValue('');
  }, [value, isTyping, isResolved, onSendMessage]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  if (isResolved) {
    return (
      <Box sx={{ px: 1.5, py: 1 }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<RestartAltIcon />}
          onClick={onNewChat}
          sx={{ borderRadius: 3 }}
        >
          Начать новый чат
        </Button>
      </Box>
    );
  }

  return (
    <TextField
      fullWidth
      size="small"
      placeholder="Введите сообщение..."
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={handleKeyDown}
      sx={{
        px: 1.5,
        py: 1,
        '& .MuiOutlinedInput-root': {
          borderRadius: 3,
        },
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              onClick={handleSend}
              disabled={isSendDisabled}
              color="primary"
              size="small"
              edge="end"
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
};
